// BoothMapCanvas Component (Issue #1028 - Phase 2B)
// Shared canvas component for rendering booth maps with zoom/pan/grid controls
// Extracted from InteractiveBoothMap.tsx (Issue #676, #749) for reuse in editing interfaces

import React, { useState, useRef, useEffect, useCallback, useId, ReactNode } from 'react';
import { vibrateLight, vibrateNotification } from '../utils/haptics';

export interface BoothMapCanvasProps {
  children: ReactNode; // SVG content to render (booths, amenities, etc.)
  viewBoxWidth?: number;
  viewBoxHeight?: number;
  initialZoom?: number;
  minZoom?: number;
  maxZoom?: number;
  showGrid?: boolean;
  showControls?: boolean;
  editMode?: boolean; // View-only vs editable mode
  className?: string;
  ariaLabel?: string;
  contentGroupRef?: React.RefObject<SVGGElement>; // Ref to the transformed content <g> for coordinate conversion
}

/**
 * Shared booth map canvas with zoom, pan, and grid functionality.
 * Supports mouse, touch gestures, and keyboard controls.
 * Can be used in view-only or edit mode for different use cases.
 */
const BoothMapCanvas: React.FC<BoothMapCanvasProps> = ({
  children,
  viewBoxWidth = 500,
  viewBoxHeight = 400,
  initialZoom = 1,
  minZoom = 0.5,
  maxZoom = 3,
  showGrid = true,
  showControls = true,
  editMode = false,
  className = '',
  ariaLabel = 'Interactive booth map with zoom and pan controls',
  contentGroupRef,
}) => {
  // Unique ID for grid pattern to prevent collisions when multiple canvases render
  const gridId = useId();

  // Mouse/touch state
  const [zoom, setZoom] = useState(initialZoom);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const svgRef = useRef<SVGSVGElement>(null);
  const internalContentGroupRef = useRef<SVGGElement>(null);

  // Touch gesture state (Issue #749)
  const [pinchDistance, setPinchDistance] = useState<number | null>(null);
  const [lastTapTime, setLastTapTime] = useState<number>(0);

  // Visual feedback for zoom limits
  const [showZoomLimitFeedback, setShowZoomLimitFeedback] = useState(false);

  // Track if canvas has been interacted with (for keyboard handler activation)
  const [hasInteracted, setHasInteracted] = useState(false);

  // Handle mouse wheel zoom
  const handleWheel = (e: React.WheelEvent<SVGSVGElement>) => {
    e.preventDefault();
    setHasInteracted(true); // Enable keyboard handlers on first interaction
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom((prevZoom) => Math.min(Math.max(prevZoom * delta, minZoom), maxZoom));
  };

  // Handle mouse down for dragging
  const handleMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    if (e.button === 0) { // Left click only
      setHasInteracted(true); // Enable keyboard handlers on first interaction
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  // Handle mouse move for dragging
  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (isDragging) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  // Handle mouse up to stop dragging
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch event handlers (Issue #749)

  // Calculate distance between two touch points (memoized for performance)
  const getTouchDistance = useCallback((touch1: React.Touch, touch2: React.Touch): number => {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }, []);

  // Calculate midpoint between two touch points (memoized for performance)
  const getTouchMidpoint = useCallback((touch1: React.Touch, touch2: React.Touch): { x: number; y: number } => {
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2,
    };
  }, []);

  // Handle touch start - supports single tap, pinch, and two-finger pan
  const handleTouchStart = (e: React.TouchEvent<SVGSVGElement>) => {
    setHasInteracted(true); // Enable keyboard handlers on first interaction
    console.debug('[BoothMapCanvas] Touch start', { touches: e.touches.length });

    // Two-finger gestures (pinch zoom, two-finger pan)
    if (e.touches.length === 2) {
      e.preventDefault();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = getTouchDistance(touch1, touch2);
      setPinchDistance(distance);

      const midpoint = getTouchMidpoint(touch1, touch2);
      setDragStart({ x: midpoint.x - pan.x, y: midpoint.y - pan.y });

      console.debug('[BoothMapCanvas] Two-finger gesture detected', { distance });
    }
    // Single finger gesture (tap or pan)
    else if (e.touches.length === 1) {
      const touch = e.touches[0];
      const currentTime = Date.now();

      // Detect double-tap (within 300ms)
      if (currentTime - lastTapTime < 300) {
        e.preventDefault();
        // Double-tap: zoom in on the tapped location
        const newZoom = Math.min(zoom * 1.5, maxZoom);
        setZoom(newZoom);
        vibrateLight();
        console.info('[BoothMapCanvas] Double-tap zoom', { newZoom });
        setLastTapTime(0); // Reset to prevent triple-tap
        return;
      }

      setLastTapTime(currentTime);

      // Start dragging for single-finger pan
      setIsDragging(true);
      setDragStart({ x: touch.clientX - pan.x, y: touch.clientY - pan.y });
    }
  };

  // Handle touch move - supports pinch zoom and two-finger pan
  const handleTouchMove = (e: React.TouchEvent<SVGSVGElement>) => {
    // Two-finger pinch zoom and pan
    if (e.touches.length === 2 && pinchDistance !== null) {
      e.preventDefault();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];

      // Pinch zoom
      const newDistance = getTouchDistance(touch1, touch2);
      const delta = newDistance / pinchDistance;
      const newZoom = Math.min(Math.max(zoom * delta, minZoom), maxZoom);

      // Trigger haptic feedback and visual indicator when reaching zoom limits
      if ((newZoom >= maxZoom && zoom < maxZoom) || (newZoom <= minZoom && zoom > minZoom)) {
        vibrateNotification();
        console.info('[BoothMapCanvas] Zoom limit reached', { newZoom });
        setShowZoomLimitFeedback(true);
        setTimeout(() => setShowZoomLimitFeedback(false), 1000);
      }

      setZoom(newZoom);
      setPinchDistance(newDistance);

      // Two-finger pan
      const midpoint = getTouchMidpoint(touch1, touch2);
      setPan({
        x: midpoint.x - dragStart.x,
        y: midpoint.y - dragStart.y,
      });

      console.debug('[BoothMapCanvas] Pinch/pan', { newZoom, pan: { x: midpoint.x - dragStart.x, y: midpoint.y - dragStart.y } });
    }
    // Single-finger pan
    else if (e.touches.length === 1 && isDragging) {
      const touch = e.touches[0];
      setPan({
        x: touch.clientX - dragStart.x,
        y: touch.clientY - dragStart.y,
      });
    }
  };

  // Handle touch end
  const handleTouchEnd = (e: React.TouchEvent<SVGSVGElement>) => {
    console.debug('[BoothMapCanvas] Touch end', { touches: e.touches.length });

    // Reset pinch state
    setPinchDistance(null);
    setIsDragging(false);
  };

  // Set up touch event listeners with proper cleanup (Issue #749)
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    // Prevent default touch behaviors (browser pinch zoom, pull-to-refresh)
    const preventDefaultTouch = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    };

    svg.addEventListener('touchstart', preventDefaultTouch, { passive: false });
    svg.addEventListener('touchmove', preventDefaultTouch, { passive: false });

    return () => {
      svg.removeEventListener('touchstart', preventDefaultTouch);
      svg.removeEventListener('touchmove', preventDefaultTouch);
    };
  }, []);

  // Keyboard navigation support for accessibility (Issue #499)
  // Scoped to canvas via onKeyDown - only active when SVG is focused
  const handleKeyDown = (e: React.KeyboardEvent<SVGSVGElement>) => {
    // Don't enable keyboard shortcuts until user has interacted with canvas
    if (!hasInteracted) {
      return;
    }

    const panStep = 20;
    const zoomStep = 0.1;

    switch (e.key) {
      case '+':
      case '=':
        // Zoom in
        e.preventDefault();
        setZoom((z) => {
          const newZoom = Math.min(z + zoomStep, maxZoom);
          if (newZoom >= maxZoom) {
            setShowZoomLimitFeedback(true);
            setTimeout(() => setShowZoomLimitFeedback(false), 1000);
            vibrateNotification();
          }
          return newZoom;
        });
        console.info('[BoothMapCanvas] Keyboard: Zoom in');
        break;
      case '-':
      case '_':
        // Zoom out
        e.preventDefault();
        setZoom((z) => {
          const newZoom = Math.max(z - zoomStep, minZoom);
          if (newZoom <= minZoom) {
            setShowZoomLimitFeedback(true);
            setTimeout(() => setShowZoomLimitFeedback(false), 1000);
            vibrateNotification();
          }
          return newZoom;
        });
        console.info('[BoothMapCanvas] Keyboard: Zoom out');
        break;
      case 'r':
      case 'R':
        // Reset zoom and pan
        e.preventDefault();
        setZoom(initialZoom);
        setPan({ x: 0, y: 0 });
        console.info('[BoothMapCanvas] Keyboard: Reset view');
        break;
      case 'ArrowUp':
        // Pan up
        e.preventDefault();
        setPan((p) => ({ x: p.x, y: p.y + panStep }));
        break;
      case 'ArrowDown':
        // Pan down
        e.preventDefault();
        setPan((p) => ({ x: p.x, y: p.y - panStep }));
        break;
      case 'ArrowLeft':
        // Pan left
        e.preventDefault();
        setPan((p) => ({ x: p.x + panStep, y: p.y }));
        break;
      case 'ArrowRight':
        // Pan right
        e.preventDefault();
        setPan((p) => ({ x: p.x - panStep, y: p.y }));
        break;
    }
  };

  // Reset view function (exposed for external control)
  const resetView = () => {
    setZoom(initialZoom);
    setPan({ x: 0, y: 0 });
  };

  return (
    <div className={`booth-map-canvas relative bg-gray-50 rounded-lg border-2 border-gray-200 overflow-hidden ${className}`}>
      {/* Controls */}
      {showControls && (
        <div className="absolute top-4 right-4 z-10 bg-white rounded-lg shadow-md p-2 space-y-2">
          <button
            onClick={() => setZoom((z) => Math.min(z * 1.2, maxZoom))}
            className="block w-full px-3 py-1 text-sm bg-primary text-white rounded hover:bg-primary-dark"
            aria-label="Zoom in on booth map"
            data-testid="zoom-in-button"
          >
            Zoom In
          </button>
          <button
            onClick={() => setZoom((z) => Math.max(z * 0.8, minZoom))}
            className="block w-full px-3 py-1 text-sm bg-primary text-white rounded hover:bg-primary-dark"
            aria-label="Zoom out on booth map"
            data-testid="zoom-out-button"
          >
            Zoom Out
          </button>
          <button
            onClick={resetView}
            className="block w-full px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
            aria-label="Reset booth map zoom and position"
            data-testid="reset-view-button"
          >
            Reset
          </button>
        </div>
      )}

      {/* Edit mode indicator */}
      {editMode && (
        <div className="absolute top-4 left-4 z-10 bg-yellow-100 border-2 border-yellow-400 rounded-lg px-3 py-1">
          <p className="text-xs font-semibold text-yellow-800">✏️ Edit Mode</p>
        </div>
      )}

      {/* SVG Canvas */}
      <svg
        ref={svgRef}
        viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
        className="w-full h-[600px] cursor-grab active:cursor-grabbing"
        role="img"
        aria-label={ariaLabel}
        tabIndex={0}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onKeyDown={handleKeyDown}
        onFocus={() => setHasInteracted(true)} // Enable keyboard shortcuts when canvas receives focus
        onMouseDownCapture={() => setHasInteracted(true)} // Capture phase to catch child drag starts
        onTouchStartCapture={() => setHasInteracted(true)} // Capture phase to catch child drag starts
        style={{
          cursor: isDragging ? 'grabbing' : 'grab',
          touchAction: 'none', // Prevent browser default touch behaviors
        }}
        data-testid="booth-map-canvas"
      >
        {/* Apply zoom and pan transformations */}
        <g
          ref={contentGroupRef || internalContentGroupRef}
          transform={`translate(${pan.x / 2}, ${pan.y / 2}) scale(${zoom})`}
        >
          {/* Grid background */}
          {showGrid && (
            <>
              <defs>
                <pattern id={gridId} width="50" height="50" patternUnits="userSpaceOnUse">
                  <path d="M 50 0 L 0 0 0 50" fill="none" stroke="#E5E7EB" strokeWidth="1" />
                </pattern>
              </defs>
              <rect width={viewBoxWidth} height={viewBoxHeight} fill={`url(#${gridId})`} />
            </>
          )}

          {/* Render children (booths, amenities, etc.) */}
          {children}
        </g>
      </svg>

      {/* Zoom limit feedback (Issue #749 - UX enhancement) */}
      {showZoomLimitFeedback && (
        <div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20 bg-gray-900 bg-opacity-75 text-white px-6 py-3 rounded-lg shadow-lg animate-fade-in"
          data-testid="zoom-limit-feedback"
        >
          <p className="text-sm font-semibold">
            {zoom >= maxZoom ? 'Maximum zoom reached' : 'Minimum zoom reached'}
          </p>
        </div>
      )}
    </div>
  );
};

export default BoothMapCanvas;
